/**
 * SocketProvider.tsx — WebSocket manager for GenericSimulator
 *
 * Overview:
 *  - Establishes a single WebSocket connection for real-time simulation data.
 *  - Skipped when dashboard is "summary" or scenario_id is missing.
 *
 * Key Functions:
 *  - create_socket()  : Creates a new WebSocket instance with the simulation URL.
 *
 * Lifecycle:
 *  - Mount   → create socket, send auth token on open, store in context.
 *  - Unmount → close the socket cleanly.
 *  - Depends on scenario_id — reconnects if scenario changes.
 */

import { ReactNode, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getEnvVar } from '../../../envUtils';
import useUrlParams from '../hooks/useUrlParams';
import useSocket from '../hooks/useSocket';

interface SocketProviderProps {
    children: ReactNode;
}

const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const { scenario_id, mission_id, dashboard } = useUrlParams();
    const { setSocket } = useSocket();

    const create_socket = (sessionId: string): WebSocket | null => {
        try {
            return new WebSocket(
                `${getEnvVar('REACT_APP_WEBSOCKET_BACKEND_BASE_URL')}test/v3/simulate/${mission_id}/scenario/${scenario_id}/visualize?sessionId=${sessionId}`
            );
        } catch (error) {
            console.error('[GenericSimulator] Socket creation failed:', error);
            return null;
        }
    };

    useEffect(() => {
        // Skip WebSocket connection in summary mode or when scenario_id is missing
        if (!scenario_id || dashboard === 'summary') return;

        const sessionId = uuidv4();
        const ws = create_socket(sessionId);
        const token = sessionStorage.getItem('authentication');

        if (!ws || !token) return;

        ws.onopen = () => {
            ws.send(JSON.stringify({ type: 'authorization', token: `${token}` }));
            setSocket(ws);
        };

        ws.onclose = () => {};

        ws.onerror = (error) => {
            console.error('[GenericSimulator] Socket Error:', error);
        };

        return () => {
            ws.close();
        };
    }, [scenario_id]); // eslint-disable-line react-hooks/exhaustive-deps

    return <>{children}</>;
};

export default SocketProvider;
